// ============================================================================
// Beacon — Syndicate ("group of equities"). A trusted circle pools capital AND
// expertise to do a bigger deal together. One leader (the sponsor) is
// compensated for running it; every member — cash or sweat — owns a real slice.
//
// Legal posture (v1): a member-managed LLC raising via Reg D 506(b) among the
// leader's PRE-EXISTING network (unlimited accredited + up to 35 sophisticated
// non-accredited; NO public advertising). Beacon is SOFTWARE ONLY: it never
// touches money (escrow/bank holds it), never gives advice, and charges a SaaS
// fee — not a cut of the raise. All of this is guarded + disclaimed in the UI.
// Same dual-mode repo pattern (cloud `syndicates` + local fallback).
// ============================================================================

import { getSupabase, isCloud } from "./supabase";

export const NON_ACCREDITED_CAP = 35; // Reg D 506(b) limit

export type MemberRole = "Sponsor" | "Manager" | "Working Partner" | "Investor";
export const MEMBER_ROLES: MemberRole[] = ["Sponsor", "Manager", "Working Partner", "Investor"];

export type CommitStatus = "invited" | "soft" | "loi" | "subscribed";
export const COMMIT_LABEL: Record<CommitStatus, string> = {
  invited: "Invited", soft: "Soft-committed", loi: "LOI signed", subscribed: "Subscribed",
};

export interface SyndicateMember {
  id: string;
  name: string;
  email?: string;
  role: MemberRole;
  cash: number; // capital committed ($)
  sweat: number; // valued sweat / expertise ($)
  sweatNote?: string; // what they bring (GC, PM, legal, capital raising…)
  accredited: boolean;
  status: CommitStatus;
}

export type SyndicateStage = "Forming" | "Raising" | "Closing" | "Active";
export const SYNDICATE_STAGES: SyndicateStage[] = ["Forming", "Raising", "Closing", "Active"];

export interface ComplianceStep { id: string; label: string; done: boolean; }

export interface Syndicate {
  id: string;
  name: string;
  leaderName: string;
  leaderEmail?: string;
  dealId?: string; // linked Beacon deal
  targetRaise: number; // cash target ($)
  entity: string; // e.g. "Member-managed LLC"
  exemption: string; // e.g. "Reg D 506(b)"
  // sponsor economics
  acqFeePct: number; // % of purchase
  amFeePct: number; // %/yr of assets
  prefPct: number; // preferred return to members
  promotePct: number; // sponsor's share of profit above the pref
  members: SyndicateMember[];
  compliance: ComplianceStep[];
  stage: SyndicateStage;
  created_at?: string;
}

let counter = 0;
export function newSyndicateId(): string { counter += 1; return "sy" + Date.now().toString(36) + counter.toString(36); }
export function newMemberId(): string { counter += 1; return "sm" + Date.now().toString(36) + counter.toString(36); }

export function defaultCompliance(): ComplianceStep[] {
  return [
    { id: "attorney", label: "Engage a securities attorney", done: false },
    { id: "llc", label: "Form the LLC (operating agreement)", done: false },
    { id: "ein", label: "Get the EIN", done: false },
    { id: "escrow", label: "Open the escrow / bank account", done: false },
    { id: "ppm", label: "Prepare the PPM & subscription agreement", done: false },
    { id: "accred", label: "Collect member accreditation reps", done: false },
    { id: "cap35", label: "Confirm ≤ 35 non-accredited members", done: false },
    { id: "subs", label: "Execute subscriptions (DocuSign)", done: false },
    { id: "formd", label: "File Form D with the SEC", done: false },
    { id: "bluesky", label: "File state blue-sky notices", done: false },
  ];
}

export function blankMember(): SyndicateMember {
  return { id: newMemberId(), name: "", role: "Investor", cash: 0, sweat: 0, sweatNote: "", accredited: false, status: "invited" };
}

export function newSyndicate(leaderName: string, name: string): Syndicate {
  return {
    id: newSyndicateId(), name, leaderName, targetRaise: 1_000_000,
    entity: "Member-managed LLC", exemption: "Reg D 506(b)",
    acqFeePct: 0.02, amFeePct: 0.015, prefPct: 0.08, promotePct: 0.2,
    members: [{ ...blankMember(), name: leaderName, role: "Sponsor", accredited: true, status: "subscribed" }],
    compliance: defaultCompliance(), stage: "Forming",
  };
}

// --- cap-table math (pure) --------------------------------------------------
export const contribution = (m: SyndicateMember): number => (m.cash || 0) + (m.sweat || 0);
export const poolTotal = (ms: SyndicateMember[]): number => ms.reduce((s, m) => s + contribution(m), 0);

/** Ownership % from combined cash + sweat contribution (0..1). */
export function ownershipPct(m: SyndicateMember, ms: SyndicateMember[]): number {
  const pool = poolTotal(ms);
  return pool > 0 ? contribution(m) / pool : 0;
}

/** Cash committed (anything past 'invited') and hard-subscribed cash. */
export function committedCash(ms: SyndicateMember[]): number {
  return ms.filter((m) => m.status !== "invited").reduce((s, m) => s + (m.cash || 0), 0);
}
export function subscribedCash(ms: SyndicateMember[]): number {
  return ms.filter((m) => m.status === "subscribed").reduce((s, m) => s + (m.cash || 0), 0);
}
export function nonAccreditedCount(ms: SyndicateMember[]): number {
  return ms.filter((m) => !m.accredited).length;
}

export interface CapSummary {
  cashTotal: number; sweatTotal: number; pool: number;
  committed: number; subscribed: number; progress: number;
  members: number; nonAccredited: number; capExceeded: boolean;
}
export function capSummary(s: Syndicate): CapSummary {
  const cashTotal = s.members.reduce((a, m) => a + (m.cash || 0), 0);
  const sweatTotal = s.members.reduce((a, m) => a + (m.sweat || 0), 0);
  const committed = committedCash(s.members);
  const na = nonAccreditedCount(s.members);
  return {
    cashTotal, sweatTotal, pool: cashTotal + sweatTotal,
    committed, subscribed: subscribedCash(s.members),
    progress: s.targetRaise > 0 ? committed / s.targetRaise : 0,
    members: s.members.length, nonAccredited: na, capExceeded: na > NON_ACCREDITED_CAP,
  };
}

export const DISCLAIMER =
  "Beacon is software, not a broker-dealer, funding portal, or law firm. It does not offer securities, give legal or " +
  "investment advice, or hold any funds — all capital stays in your escrow/bank. This summary is a working draft to " +
  "review with your own securities attorney, who is responsible for the offering and its compliance.";

// --- the one-page structure document (pure → branded HTML) ------------------
const esc = (s: string) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]!));
const money = (n: number) => "$" + Math.round(n || 0).toLocaleString();
const pct = (n: number) => (n * 100).toFixed(n < 0.1 ? 1 : 0) + "%";

export function structureDocHtml(s: Syndicate, dealName?: string): string {
  const cap = capSummary(s);
  const rows = s.members.map((m) => `<tr>
    <td style="padding:6px 8px;border-bottom:1px solid #eadfce">${esc(m.name || "—")}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #eadfce">${esc(m.role)}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #eadfce;text-align:right">${money(m.cash)}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #eadfce;text-align:right">${money(m.sweat)}${m.sweatNote ? ` <span style="color:#8a7a5a">(${esc(m.sweatNote)})</span>` : ""}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #eadfce;text-align:right">${pct(ownershipPct(m, s.members))}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #eadfce">${m.accredited ? "Accredited" : "Non-accred."}</td>
  </tr>`).join("");

  const sect = (h: string, body: string) => `<div style="margin:16px 0"><div style="font:700 11px Inter,sans-serif;letter-spacing:.1em;text-transform:uppercase;color:#8C6A34;margin-bottom:6px">${h}</div>${body}</div>`;
  const kv = (k: string, v: string) => `<div style="font-size:13.5px;margin:3px 0"><span style="color:#6A7A72">${k}:</span> <b style="color:#17352B">${v}</b></div>`;

  return `<div style="font-family:Georgia,'Times New Roman',serif;max-width:720px;margin:0 auto;color:#1A211E;font-size:14px">
  <div style="border-bottom:2px solid #B08948;padding-bottom:12px;margin-bottom:18px">
    <div style="font:700 16px Inter,sans-serif;color:#17352B">Syndicate Structure Summary</div>
    <div style="font-size:12px;color:#6A7A72;margin-top:2px">Prepared in Beacon for attorney review · working draft</div>
  </div>
  ${sect("The group", kv("Group name", esc(s.name)) + kv("Lead sponsor", esc(s.leaderName)) + (dealName ? kv("Target property", esc(dealName)) : "") + kv("Target raise (cash)", money(s.targetRaise)) + kv("Stage", esc(s.stage)))}
  ${sect("Proposed structure", kv("Entity", esc(s.entity)) + kv("Offering exemption", esc(s.exemption)) + `<div style="font-size:13px;color:#37413c;margin-top:6px;line-height:1.55">Private offering to the sponsor's pre-existing network — <b>no general solicitation</b>. Unlimited accredited investors plus up to <b>${NON_ACCREDITED_CAP}</b> sophisticated non-accredited members, each with full PPM disclosure. All members actively contribute capital and/or expertise.</div>`)}
  ${sect("Sponsor economics", kv("Acquisition fee", pct(s.acqFeePct) + " of purchase") + kv("Asset-management fee", pct(s.amFeePct) + " / yr") + kv("Preferred return to members", pct(s.prefPct)) + kv("Sponsor promote (carry)", pct(s.promotePct) + " of profit above the pref"))}
  ${sect("Cap table — cash + sweat equity", `<table style="width:100%;border-collapse:collapse;font-family:Inter,sans-serif;font-size:12.5px">
    <thead><tr style="text-align:left;color:#6A7A72">
      <th style="padding:6px 8px;border-bottom:1.5px solid #cdbf9f">Member</th><th style="padding:6px 8px;border-bottom:1.5px solid #cdbf9f">Role</th>
      <th style="padding:6px 8px;border-bottom:1.5px solid #cdbf9f;text-align:right">Cash</th><th style="padding:6px 8px;border-bottom:1.5px solid #cdbf9f;text-align:right">Sweat</th>
      <th style="padding:6px 8px;border-bottom:1.5px solid #cdbf9f;text-align:right">Ownership</th><th style="padding:6px 8px;border-bottom:1.5px solid #cdbf9f">Status</th></tr></thead>
    <tbody>${rows}</tbody></table>
    <div style="font-family:Inter,sans-serif;font-size:12px;color:#6A7A72;margin-top:8px">Committed cash ${money(cap.committed)} of ${money(s.targetRaise)} target · ${cap.members} members · ${cap.nonAccredited} non-accredited${cap.capExceeded ? " <b style='color:#A6503C'>(exceeds the 35 cap — restructure)</b>" : " (within the 35 cap)"}</div>`)}
  ${sect("Open items to confirm with counsel", `<ul style="font-family:Inter,sans-serif;font-size:12.5px;color:#37413c;line-height:1.6;padding-left:18px;margin:0">${s.compliance.map((c) => `<li>${c.done ? "✓ " : "☐ "}${esc(c.label)}</li>`).join("")}</ul>`)}
  <div style="border-top:1px solid #DED7C6;margin-top:18px;padding-top:12px;font-family:Inter,sans-serif;font-size:11px;color:#6A7A72;line-height:1.55">${esc(DISCLAIMER)}</div>
</div>`;
}

/** Email the structure doc (e.g. to your securities attorney). Cloud-only. */
export async function emailStructureDoc(s: Syndicate, to: string, dealName?: string): Promise<void> {
  if (!isCloud()) throw new Error("Email needs cloud mode (the Resend key lives in the Edge Function).");
  const sb = getSupabase()!;
  const html = structureDocHtml(s, dealName);
  const { error } = await sb.functions.invoke("notify", { body: { to, subject: `Syndicate structure — ${s.name}`, html } });
  if (error) throw new Error(error.message || "email failed");
}

// --- persistence (cloud `syndicates` + local fallback) ----------------------
const LS = "beacon_syndicates";
function localAll(): Syndicate[] {
  try { const raw = localStorage.getItem(LS); if (raw) return JSON.parse(raw) as Syndicate[]; } catch { /* ignore */ }
  return [];
}
function localSave(all: Syndicate[]) { try { localStorage.setItem(LS, JSON.stringify(all)); } catch { /* ignore */ } }

export async function listSyndicates(): Promise<Syndicate[]> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { data, error } = await sb.from("syndicates").select("*").order("created_at");
      if (error) throw error;
      if (data) return data.map((r: { id: string; name: string; payload: Omit<Syndicate, "id" | "name"> }) => ({ id: r.id, name: r.name, ...r.payload }));
    } catch { /* fall through */ }
  }
  return localAll();
}

export async function saveSyndicate(s: Syndicate): Promise<void> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { id, name, ...payload } = s;
      const { error } = await sb.from("syndicates").upsert({ id, name, payload });
      if (error) throw error;
      return;
    } catch { /* fall through */ }
  }
  const all = localAll();
  const i = all.findIndex((x) => x.id === s.id);
  if (i >= 0) all[i] = s; else all.push(s);
  localSave(all);
}

export async function deleteSyndicate(id: string): Promise<void> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { error } = await sb.from("syndicates").delete().eq("id", id);
      if (error) throw error;
      return;
    } catch { /* fall through */ }
  }
  localSave(localAll().filter((x) => x.id !== id));
}
